import { useGraphQLMutation } from '@opengov/bnp-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckboxTreeNodeId } from '@opengov/components-checkbox-tree';
import { IPublicationNavMenuStory } from '@opengov/stories-shared/types';
import { useCallback } from 'react';

import { graphql } from '../../../../../gql-generated/hasura';
import {
  InsertStoriesForMenuIdForOnlinePublicationMutation,
  InsertStoriesForMenuIdForOnlinePublicationMutationVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { getUserById } from '../../../../routes/hasura';
import { postHasuraGraphql } from '../../../../utils/hasuraGraphql';
import { useToast } from '../../../Toast/context/useToast.context';

import { setupOnlinePublicationQueryKeys } from './setupOnlinePublicationQueryKeys';

const InsertStoriesForMenuIdForOnlinePublicationDocument = graphql(`
  mutation InsertStoriesForMenuIdForOnlinePublication(
    $objects: [publicationNavigationMenuStories_insert_input!]!
  ) {
    storiesList: insert_publicationNavigationMenuStories(objects: $objects) {
      affected_rows
      returning {
        id
      }
    }
  }
`);

// Dynamic reorder mutation (same shape as `useOrganizeMenuStoriesList`).
// Inlined here so this hook stays self-contained — the call always runs
// right after the insert, so coupling the two avoids surfacing reorder as
// a separate caller concern.
const buildReorderMenuStoriesMutation = (orderedStoriesIds: string[]): string => `
  mutation reorderMenuStoriesList {
    ${orderedStoriesIds
      .map(
        (menuStoryId, index) => `
      story${index}: update_publicationNavigationMenuStories_by_pk(
        _set: { order: ${index + 1} }
        pk_columns: { id: "${menuStoryId}" }
      ) {
        id
        order
      }`
      )
      .join('')}
  }
`;

export type UseSetStoriesForMenuIdMessages = {
  successTitle: string;
  successBody?: string;
  errorTitle: string;
  errorBody?: string;
};

export type SetStoriesForMenuIdParams = {
  publicationId: string;
  menuId: string;
  storyIds: CheckboxTreeNodeId[];
  menuStories: IPublicationNavMenuStory[];
  userId: number;
};

export function useSetStoriesForMenuId(messages: UseSetStoriesForMenuIdMessages) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  const insertMutation = useGraphQLMutation<
    InsertStoriesForMenuIdForOnlinePublicationMutation,
    InsertStoriesForMenuIdForOnlinePublicationMutationVariables
  >({
    mutationKey: [...setupOnlinePublicationQueryKeys.all, 'setStoriesForMenuId', 'insert'],
    document: InsertStoriesForMenuIdForOnlinePublicationDocument,
    url: HASURA_SERVICE_URL
  });

  const reorderMutation = useMutation<unknown, Error, { orderedStoriesIds: string[] }>({
    mutationKey: [...setupOnlinePublicationQueryKeys.all, 'setStoriesForMenuId', 'reorder'],
    mutationFn: ({ orderedStoriesIds }) =>
      postHasuraGraphql(buildReorderMenuStoriesMutation(orderedStoriesIds), {})
  });

  const setStoriesForMenuId = useCallback(
    async (params: SetStoriesForMenuIdParams): Promise<boolean> => {
      const { publicationId, menuId, storyIds, menuStories, userId } = params;
      try {
        const createdBy = await getUserById(userId);
        const insertResult = await insertMutation.mutateAsync({
          objects: storyIds.map((storyId) => ({ menuId, storyId: storyId as string, createdBy }))
        });
        const returning = insertResult?.storiesList?.returning;
        if (!returning) {
          throw new Error('Insert returned no rows');
        }
        const insertedIds = returning.map((row) => row.id);
        const orderedStoriesIds = menuStories.map((story) => story.id).concat(insertedIds);
        await reorderMutation.mutateAsync({ orderedStoriesIds });

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: setupOnlinePublicationQueryKeys.menuStories({ menuId })
          }),
          queryClient.invalidateQueries({
            queryKey: setupOnlinePublicationQueryKeys.navigationMenus({ publicationId })
          })
        ]);
        toggleToast({
          title: messages.successTitle,
          body: messages.successBody,
          severity: 'success'
        });
        return true;
      } catch {
        toggleToast({
          title: messages.errorTitle,
          body: messages.errorBody,
          severity: 'error'
        });
        return false;
      }
    },
    [insertMutation, reorderMutation, queryClient, messages, toggleToast]
  );

  return {
    setStoriesForMenuId,
    isLoading: insertMutation.isLoading || reorderMutation.isLoading
  };
}
